import {Injectable} from '@angular/core';
import {ApiService, EnvironmentModel} from '@cas-hub/acabim-common-client';
import {HttpClient, HttpParams} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {
    EditAccountModel, FinancialChartAccountOptions,
    FinancialChartDetails,
    FinancialChartModel
} from '../_models/financial-account.model';

@Injectable({
    providedIn: 'root'
})
export class FinancialChartService extends ApiService {

    constructor(protected client: HttpClient, protected environment: EnvironmentModel) {
        super(client, environment);
    }

    protected controller = 'financial/organisation/chart-of-accounts';

    public getAllClasses(includeInactive: boolean = false, includeAccounts: boolean = false): Observable<FinancialChartModel[]> {
        const params = new HttpParams()
            .set('include_inactive', includeInactive ? '1' : '0')
            .set('include_accounts', includeAccounts ? '1' : '0');
        return this.client.get(this.url(''), {
            params,
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => (data as any).data as FinancialChartModel[]));
    }

    public addNewClass(identifier: number, name: string, description: string): Observable<FinancialChartDetails> {
        const body = {identifier, name, description};
        return this.client.post(this.url('class'), body, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => data as FinancialChartDetails));
    }

    public updateClass(id: number, identifier: number, name: string, description: string): Observable<FinancialChartDetails> {
        const body = {identifier, name, description};
        return this.client.post(this.url('class/' + id), body, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => data as FinancialChartDetails));
    }

    public toggleClassStatus(id: number | undefined): Observable<FinancialChartDetails> {
        return this.client.post(this.url('class/' + id + '/status'), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => data as FinancialChartDetails));
    }

    public addNewAccount(classId: number, editData: EditAccountModel): Observable<any> {
        return this.client.post(this.url('class/' + classId + '/account'), editData, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public updateAccount(classId: number, editData: EditAccountModel, accountId: number): Observable<any> {
        return this.client.post(this.url('class/' + classId + '/account/' + accountId), editData, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public toggleAccountStatus(classId: number | undefined, accountId: number | undefined): Observable<any> {
        return this.client.post(this.url('class/' + classId + '/account/' + accountId + '/status'), {}, {
            headers: this.defaultHeaders,
            responseType: 'json'
        });
    }

    public getValueTypes(): Observable<string[]> {
        return this.client.get(this.url('financial/utilities/transaction-types', true), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => ((data as any).data as string[])));
    }

    public getAccountTypes(): Observable<string[]> {
        return this.client.get(this.url('financial/utilities/account-types', true), {
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => ((data as any).data as string[])));
    }

    public getChartOfAccountsAsOptions(includeInactive: boolean = false, includeAccounts: boolean = false): Observable<FinancialChartAccountOptions[]> {
        const params = new HttpParams()
            .set('include_inactive', includeInactive ? '1' : '0')
            .set('include_accounts', includeAccounts ? '1' : '0');
        return this.client.get(this.url('options'), {
            params,
            headers: this.defaultHeaders,
            responseType: 'json'
        }).pipe(map(data => (data as any).data as FinancialChartAccountOptions[]));
    }
}

